/**
 * Keeps a plain-text author's line breaks visible once their text is rendered
 * as CommonMark.
 *
 * Issue #13707: feedback messages used to render inside a
 * `whitespace-pre-wrap` `<p>`, where every single `\n` was a visible line
 * break. CommonMark collapses a lone `\n` into a space, so swapping the `<p>`
 * for a markdown renderer reflows every existing plain-prose report — the
 * majority of them — into one run-on paragraph. That is a visible regression
 * for the reports that contain no markdown at all, i.e. the ones that were
 * rendering correctly before the fix.
 *
 * This converts a lone `\n` between two non-blank lines into a CommonMark hard
 * break (two trailing spaces) so the rendered output matches what
 * `whitespace-pre-wrap` showed, while everything a markdown author wrote on
 * purpose — paragraph splits, lists, fences — keeps its meaning.
 *
 * Deliberately dependency-free rather than adding `remark-breaks`, following
 * the precedent its sibling `strip-inline-markdown.ts` records for exactly
 * this trade: promoting a package to a direct dependency to do one small,
 * well-specified text transform is a worse trade than a small function with
 * tests. (`remark-breaks` is also not equivalent — it rewrites every soft
 * break at the mdast level including inside constructs this must not touch.)
 *
 * The non-obvious rules, all pinned by tests:
 *
 * 1. **Verbatim regions are copied byte-for-byte.** A fenced code block and an
 *    indented code block must not gain trailing whitespace: the whole point of
 *    a code block is that its bytes are what the author typed. Note the
 *    asymmetry a fence has: an OPENING fence may carry an info string, but a
 *    CLOSING one may be followed only by whitespace. Treating ` ```js ` inside
 *    an open block as a closer ends the block early, hardens the code that
 *    follows as if it were prose, and leaves the real closer to read as a new
 *    opener that swallows the rest of the message.
 * 2. **A line that already ends a break is left alone** — two trailing spaces,
 *    or an odd number of trailing backslashes (CommonMark's other hard-break
 *    spelling). Appending to those either does nothing or corrupts them.
 * 3. **Blank-line paragraph boundaries are never touched.** A hard break at
 *    the end of a block is stripped by the renderer anyway, so adding one
 *    there would be noise in the source with no effect on the output.
 *
 * Known limitation: a list-item continuation that is separated from its item by
 * a blank line AND indented four spaces is read as an indented code block here,
 * so its soft breaks are preserved as-is rather than hardened. Detecting that
 * correctly needs real block-level parsing, which is the dependency this file
 * exists to avoid; the failure mode is a missing line break in a construct
 * essentially absent from feedback prose.
 *
 * @see src/components/markdown/strip-inline-markdown.ts — the sibling this
 *   mirrors, and the source of the dependency-free precedent
 * @see src/components/markdown-content.tsx — the renderer this feeds
 */

/** A fence opener/closer: up to 3 spaces, then 3+ backticks or tildes. */
const FENCE_LINE = /^ {0,3}(`{3,}|~{3,})(.*)$/

/** Only whitespace. */
const BLANK_LINE = /^[ \t]*$/

/** Four spaces or a tab — the indented-code-block opener. */
const INDENTED_LINE = /^(?: {4}|\t)/

/** Two or more trailing spaces: CommonMark's hard break. */
const TRAILING_HARD_BREAK = / {2,}$/

/** Trailing backslashes, counted to tell `a\` from `a\\`. */
const TRAILING_BACKSLASHES = /\\+$/

/** CommonMark's other hard break: a line ending in an unescaped backslash. */
function endsWithBackslashBreak(line: string): boolean {
  const match = line.match(TRAILING_BACKSLASHES)
  return match ? match[0].length % 2 === 1 : false
}

interface OpenFence {
  char: string
  length: number
  /**
   * Whether this line may CLOSE an open fence. An opening fence may carry an
   * info string (` ```js `), but a closing one may be followed only by
   * whitespace — so ` ```js ` inside an open block is content, not a closer.
   * Without this the block is closed early, the code after it is treated as
   * prose and gains trailing spaces, and the real closer reads as a new
   * opener that swallows the rest of the message.
   */
  closes: boolean
}

/** Reads a line as a fence marker, or `null` if it is not one. */
function readFence(line: string): OpenFence | null {
  const match = line.match(FENCE_LINE)
  if (!match) return null
  const marker = match[1]
  const info = match[2]
  // A backtick fence's info string may not itself contain a backtick, so
  // `` ```a`b `` is a paragraph, not a fence.
  if (marker[0] === '`' && info.includes('`')) return null
  return {
    char: marker[0],
    length: marker.length,
    closes: BLANK_LINE.test(info),
  }
}

/**
 * Rewrites every soft line break in `source` as a CommonMark hard break,
 * leaving code blocks, paragraph boundaries and existing hard breaks alone.
 */
export function preserveSoftBreaks(source: string): string {
  if (!source) return source

  const lines = source.replace(/\r\n?/g, '\n').split('\n')
  const out: string[] = []

  let openFence: OpenFence | null = null
  let inIndentedCode = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const isBlank = BLANK_LINE.test(line)

    // --- Verbatim region: an open fence swallows everything until it closes.
    if (openFence) {
      out.push(line)
      const closing = readFence(line)
      if (
        closing &&
        closing.closes &&
        closing.char === openFence.char &&
        closing.length >= openFence.length
      ) {
        openFence = null
      }
      continue
    }

    const fence = readFence(line)
    if (fence) {
      openFence = fence
      out.push(line)
      continue
    }

    // --- Verbatim region: an indented code block, which runs until a
    //     non-blank line returns to the left margin.
    if (inIndentedCode) {
      if (isBlank || INDENTED_LINE.test(line)) {
        out.push(line)
        continue
      }
      inIndentedCode = false
    } else if (
      INDENTED_LINE.test(line) &&
      // Indented code cannot interrupt a paragraph, so it only opens at the
      // start of the document or after a blank line. Without this guard an
      // indented stack trace under a prose line would be read as code and
      // collapse — the regression this file exists to prevent.
      (i === 0 || BLANK_LINE.test(lines[i - 1]))
    ) {
      inIndentedCode = true
      out.push(line)
      continue
    }

    const next = lines[i + 1]
    const nextIsBlank = next === undefined || BLANK_LINE.test(next)

    if (
      isBlank ||
      nextIsBlank ||
      TRAILING_HARD_BREAK.test(line) ||
      endsWithBackslashBreak(line)
    ) {
      out.push(line)
      continue
    }

    out.push(`${line}  `)
  }

  return out.join('\n')
}
